import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  OffersFiltersQuery,
  OffersQuery,
  OffersState,
  OfferSortParameter,
  OfferStatusCode,
  CreateOffer,
  ChangeOffer,
} from "./models/models";
import { MarkWorkCompleteRequest } from "../servicing/models/executionModels";
import offersApi from "./api/offersApi";
import offerExecutionApi from "./api/offerExecutionApi";

export const getOffers = createAsyncThunk(
  "customerOffersSlice/getOffers",
  async (query: OffersQuery) => {
    return offersApi.getOffers(query);
  }
);

export const getOffer = createAsyncThunk(
  "customerOffersSlice/getOffer",
  async (id: string) => {
    return offersApi.getOffer(id);
  }
);

export const getOffersFilters = createAsyncThunk(
  "customerOffersSlice/getOffersFilters",
  async (query: OffersFiltersQuery) => {
    return offersApi.getOffersFilters(query);
  }
);

// Lightweight stats thunk — reuses the filters endpoint to compute total active offers
// (excludes rejected, expired, withdrawn, archived)
const terminalStatuses = new Set([
  OfferStatusCode.rejected,
  OfferStatusCode.expired,
  OfferStatusCode.withdrawn,
  OfferStatusCode.archived,
]);

export const getOfferStats = createAsyncThunk(
  "customerOffersSlice/getOfferStats",
  async () => {
    const filters = await offersApi.getOffersFilters({});
    const totalActiveOffers = filters.statusCodes
      .filter((f) => !terminalStatuses.has(f.id))
      .reduce((sum, f) => sum + f.value, 0);
    return { totalActiveOffers };
  }
);

export const createOffer = createAsyncThunk(
  "customerOffersSlice/createOffer",
  async (model: CreateOffer) => {
    return offersApi.createOffer(model);
  }
);

export const changeOffer = createAsyncThunk(
  "customerOffersSlice/changeOffer",
  async ({ offerId, model }: { offerId: string; model: ChangeOffer }) => {
    return offersApi.changeOffer(offerId, model);
  }
);

export const submitOffer = createAsyncThunk(
  "customerOffersSlice/submitOffer",
  async (id: string) => {
    return offersApi.submitOffer(id);
  }
);

export const withdrawOffer = createAsyncThunk(
  "customerOffersSlice/withdrawOffer",
  async (id: string) => {
    return offersApi.withdrawOffer(id);
  }
);

export const deleteOffer = createAsyncThunk(
  "customerOffersSlice/deleteOffer",
  async (id: string) => {
    return offersApi.deleteOffer(id);
  }
);

export const archiveOffer = createAsyncThunk(
  "customerOffersSlice/archiveOffer",
  async (id: string) => {
    return offersApi.archiveOffer(id);
  }
);

export const unarchiveOffer = createAsyncThunk(
  "customerOffersSlice/unarchiveOffer",
  async (id: string) => {
    return offersApi.unarchiveOffer(id);
  }
);

export const confirmOffer = createAsyncThunk(
  "customerOffersSlice/confirmOffer",
  async ({
    offerId,
    data,
  }: {
    offerId: string;
    data: {
      arrivalDate: string;
      arrivalTimeFrom: string;
      arrivalTimeTo: string;
      message?: string;
    };
  }) => {
    return offersApi.confirmOffer(offerId, data);
  }
);

export const startWork = createAsyncThunk(
  "customerOffersSlice/startWork",
  async (offerId: string) => {
    return offerExecutionApi.startWork(offerId);
  }
);

export const addProgressUpdate = createAsyncThunk(
  "customerOffersSlice/addProgressUpdate",
  async ({ offerId, message }: { offerId: string; message: string }) => {
    return offerExecutionApi.addProgressUpdate(offerId, message);
  }
);

export const markWorkComplete = createAsyncThunk(
  "customerOffersSlice/markWorkComplete",
  async ({
    offerId,
    data,
  }: {
    offerId: string;
    data: MarkWorkCompleteRequest;
  }) => {
    return offerExecutionApi.markWorkComplete(offerId, data);
  }
);

const initialState: OffersState = {
  offersQuery: {
    statusCodes: [],
    pageSize: 10,
    pageNo: 1,
    sortBy: OfferSortParameter.createdDate,
    sortDesc: true,
  },
  offers: undefined,
  offersFilters: undefined,
  offerStats: undefined,
  offer: undefined,
  loading: false,
  success: undefined,
  error: undefined,
};

const customerOffersSlice = createSlice({
  name: "customerOffersSlice",
  initialState,
  reducers: {
    changeOffersQuery: (state, action) => {
      state.offersQuery = action.payload;
    },
    clearOffer: (state) => {
      state.offer = undefined;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // getOffers
      .addCase(getOffers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOffers.fulfilled, (state, { payload }) => {
        state.offers = payload;
        state.loading = false;
      })
      .addCase(getOffers.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // getOffersFilters
      .addCase(getOffersFilters.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOffersFilters.fulfilled, (state, { payload }) => {
        state.offersFilters = payload;
        state.loading = false;
      })
      .addCase(getOffersFilters.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // getOfferStats (lightweight — does not set loading to avoid spinner flicker)
      .addCase(getOfferStats.fulfilled, (state, { payload }) => {
        state.offerStats = payload;
      })
      // getOffer
      .addCase(getOffer.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOffer.fulfilled, (state, { payload }) => {
        state.offer = payload;
        state.loading = false;
      })
      .addCase(getOffer.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // createOffer
      .addCase(createOffer.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(createOffer.fulfilled, (state, { payload }) => {
        state.offer = payload;
        state.loading = false;
      })
      .addCase(createOffer.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // changeOffer
      .addCase(changeOffer.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(changeOffer.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changeOffer.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // submitOffer
      .addCase(submitOffer.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(submitOffer.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(submitOffer.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // withdrawOffer
      .addCase(withdrawOffer.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(withdrawOffer.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(withdrawOffer.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // deleteOffer
      .addCase(deleteOffer.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(deleteOffer.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteOffer.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // archiveOffer
      .addCase(archiveOffer.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(archiveOffer.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(archiveOffer.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // unarchiveOffer
      .addCase(unarchiveOffer.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(unarchiveOffer.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(unarchiveOffer.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // confirmOffer
      .addCase(confirmOffer.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(confirmOffer.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(confirmOffer.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // startWork
      .addCase(startWork.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(startWork.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(startWork.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // addProgressUpdate
      .addCase(addProgressUpdate.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(addProgressUpdate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addProgressUpdate.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // markWorkComplete
      .addCase(markWorkComplete.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(markWorkComplete.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(markWorkComplete.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });
  },
});

export const {
  changeOffersQuery,
  clearOffer,
  setSuccess,
  setLoading,
  setError,
  clearError,
} = customerOffersSlice.actions;

export default customerOffersSlice.reducer;
